import * as ort from 'onnxruntime-web';
import type { DetectionBox, DetectionClass, DetectionResult, Detector } from './Detector';
import { boxIoU } from './Detector';

export interface YoloDetectorOptions {
  modelUrl: string;
  inputSize?: number;
  minScore?: number;
  iouThreshold?: number;
  maxDetections?: number;
}

const COCO_PERSON = 0;
const COCO_SPORTS_BALL = 32;

export class YoloDetector implements Detector {
  readonly id = 'yolov8-onnx';
  readonly label = 'YOLOv8n (ONNX)';
  private session: ort.InferenceSession | null = null;
  private readonly cropCanvas: OffscreenCanvas;
  private readonly inputSize: number;
  private readonly minScore: number;
  private readonly iouThreshold: number;
  private readonly maxDetections: number;

  constructor(private readonly opts: YoloDetectorOptions) {
    this.inputSize = opts.inputSize ?? 640;
    this.minScore = opts.minScore ?? 0.3;
    this.iouThreshold = opts.iouThreshold ?? 0.45;
    this.maxDetections = opts.maxDetections ?? 30;
    this.cropCanvas = new OffscreenCanvas(this.inputSize, this.inputSize);
  }

  async initialize(): Promise<void> {
    if (this.session) return;
    this.session = await ort.InferenceSession.create(this.opts.modelUrl, {
      executionProviders: ['wasm'],
    });
  }

  async detect(video: HTMLVideoElement): Promise<DetectionResult> {
    if (!this.session) throw new Error('YoloDetector not initialized');
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w <= 0 || h <= 0) return { detections: [], inferenceTimeMs: 0 };

    const { tensor, scale, padX, padY } = this.preprocess(video, w, h);
    const t0 = performance.now();
    const outputs = await this.session.run({
      images: tensor,
    });
    const inferenceTimeMs = performance.now() - t0;
    const output = outputs[Object.keys(outputs)[0]];
    const detections = this.postprocess(output, w, h, scale, padX, padY);
    return { detections, inferenceTimeMs };
  }

  dispose(): void {
    this.session?.release?.().catch(() => undefined);
    this.session = null;
  }

  private preprocess(
    video: HTMLVideoElement,
    srcW: number,
    srcH: number,
  ): { tensor: ort.Tensor; scale: number; padX: number; padY: number } {
    const ctx = this.cropCanvas.getContext('2d');
    if (!ctx) throw new Error('OffscreenCanvas 2d context unavailable');
    const scale = Math.min(this.inputSize / srcW, this.inputSize / srcH);
    const newW = srcW * scale;
    const newH = srcH * scale;
    const padX = (this.inputSize - newW) / 2;
    const padY = (this.inputSize - newH) / 2;
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, this.inputSize, this.inputSize);
    ctx.drawImage(video, 0, 0, srcW, srcH, padX, padY, newW, newH);
    const imageData = ctx.getImageData(0, 0, this.inputSize, this.inputSize);
    const rgb = new Float32Array(3 * this.inputSize * this.inputSize);
    const plane = this.inputSize * this.inputSize;
    for (let i = 0; i < plane; i += 1) {
      const base = i * 4;
      rgb[i] = imageData.data[base] / 255;
      rgb[i + plane] = imageData.data[base + 1] / 255;
      rgb[i + 2 * plane] = imageData.data[base + 2] / 255;
    }
    const tensor = new ort.Tensor('float32', rgb, [1, 3, this.inputSize, this.inputSize]);
    return { tensor, scale, padX, padY };
  }

  private postprocess(
    out: ort.Tensor,
    srcW: number,
    srcH: number,
    scale: number,
    padX: number,
    padY: number,
  ): DetectionBox[] {
    const dims = out.dims;
    if (dims.length !== 3) return [];
    const data = out.data as Float32Array;
    const channels = dims[1];
    const anchors = dims[2];
    const numClasses = channels - 4;

    const candidates: {
      cls: DetectionClass;
      score: number;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    }[] = [];

    for (let i = 0; i < anchors; i += 1) {
      let bestScore = 0;
      let bestCls = -1;
      for (let c = 0; c < numClasses; c += 1) {
        const s = data[(4 + c) * anchors + i];
        if (s > bestScore) {
          bestScore = s;
          bestCls = c;
        }
      }
      if (bestScore < this.minScore) continue;
      let mapped: DetectionClass | null = null;
      if (bestCls === COCO_PERSON) mapped = 'person';
      else if (bestCls === COCO_SPORTS_BALL) mapped = 'ball';
      if (!mapped) continue;

      const cx = data[i];
      const cy = data[anchors + i];
      const w = data[2 * anchors + i];
      const h = data[3 * anchors + i];
      const x1 = cx - w / 2;
      const y1 = cy - h / 2;
      const x2 = cx + w / 2;
      const y2 = cy + h / 2;
      candidates.push({ cls: mapped, score: bestScore, x1, y1, x2, y2 });
    }

    candidates.sort((a, b) => b.score - a.score);

    const kept: typeof candidates = [];
    for (const c of candidates) {
      if (kept.length >= this.maxDetections) break;
      let suppress = false;
      for (const k of kept) {
        if (k.cls !== c.cls) continue;
        const boxA = {
          x: c.x1,
          y: c.y1,
          w: c.x2 - c.x1,
          h: c.y2 - c.y1,
        };
        const boxB = {
          x: k.x1,
          y: k.y1,
          w: k.x2 - k.x1,
          h: k.y2 - k.y1,
        };
        if (boxIoU(boxA, boxB) > this.iouThreshold) {
          suppress = true;
          break;
        }
      }
      if (!suppress) kept.push(c);
    }

    return kept.map((c) => {
      const x = (c.x1 - padX) / scale;
      const y = (c.y1 - padY) / scale;
      const w = (c.x2 - c.x1) / scale;
      const h = (c.y2 - c.y1) / scale;
      return {
        class: c.cls,
        x: Math.max(0, x) / srcW,
        y: Math.max(0, y) / srcH,
        w: Math.min(srcW, w) / srcW,
        h: Math.min(srcH, h) / srcH,
        score: c.score,
      };
    });
  }
}
